import assocPath from "ramda/es/assocPath";
import isNotEmpty from "ramda-extension/es/isNotEmpty";
import { useCallback, useEffect, useRef } from 'react';
import { useApmInstance } from '../../hooks';
import { instrumentTypes } from '../constants';
import { addBasicInstrumentationHandler } from '../create';
import { createEventDescription } from '../utils/description';
import { getInstrumentTransformer } from '../utils/transformers';
const defaultBreadcrumbTransformer = ({ category })=>category === 'DOM';
const useMutableBreadcrumbs = ({ max = 100 } = {})=>{
    const breadcrumbsRef = useRef([]);
    const set = useCallback((event)=>{
        const breadcrumbs = breadcrumbsRef.current;
        if (breadcrumbs.length >= max) {
            breadcrumbs.pop();
        }
        breadcrumbs.push(event);
    }, [
        max
    ]);
    const get = useCallback(()=>breadcrumbsRef.current, []);
    return [
        get,
        set
    ];
};
/**
 * Provides an initialization of instrumentations breadcrumbs buffer. When used this hook will buffer breadcrumbs based on watched instruments.
 * When error happens the breadcrumbs will be then send alongside error object to apm-server
 *
 * @documented
 * @category APM
 * @import import { useBreadcrumbs } from '@gef-ui/apm/instruments/hooks'
 * @param {String[]} instruments - Array of instruments that we want to track.
 * @param {Object} options
 * @param {Number} [options.max=100] - max size of instrumentations breadcrubs buffer. Will limit the total breadcrumbs that will be send to APM
 * @param {Object} options.apm - if APM is not provided by context
 * @param {Object} options.additionalHandlers - set of additional instruments handlers, for example custom instrument for creating error events and os on
 * @param {Object} options.breadcrumbTransformers - set of custom breadcrumb transformer functions to filter/edit events for given instrument type
 * 													functions receive and object with { category, description, timestamp }; returning null means that event won't be sent
 * @param {Function} options.transactionBreadcrumbsFilter - function that will be called with transaction object and breadcrumbs array as arguments. Result will be included in each transaction. Use with caution
 * @example
 *
 * useBreadcrumbs([instrumentTypes.DOM, instrumentTypes.APM_TRANSACTION], { max: 50 });
 *
 */ export const useBreadcrumbs = (instruments, options = {})=>{
    const { additionalHandlers, apm: optionsApm, breadcrumbTransformers, transactionBreadcrumbsFilter = defaultBreadcrumbTransformer } = options;
    const apm = useApmInstance() ?? optionsApm;
    const [getBreadcrumbs, setBreadcrumb] = useMutableBreadcrumbs(options);
    const logTransactions = instruments.includes(instrumentTypes.APM_TRANSACTION);
    useEffect(()=>{
        if (apm && logTransactions) {
            const observeType = 'transaction:end';
            const transationEndHandler = ({ name })=>{
                setBreadcrumb(createEventDescription(instrumentTypes.APM_TRANSACTION, {
                    name
                }));
            };
            apm.observe(observeType, transationEndHandler);
            return ()=>{
                // Yea, there is no cleaner way to clean the unwanted observers :(
                // serviceFactory', 'instances', 'ConfigService
                const apmObservers = apm.serviceFactory.instances.ConfigService.events.observers;
                apmObservers[observeType] = apmObservers[observeType].filter((transactionObserver)=>transactionObserver !== transationEndHandler);
            };
        }
    }, [
        apm,
        logTransactions,
        setBreadcrumb
    ]);
    useEffect(()=>{
        if (apm) {
            const filterHandler = (payload)=>{
                if (isNotEmpty(payload.errors)) {
                    const breadcrumbs = getBreadcrumbs();
                    payload.errors = payload.errors.map(assocPath([
                        'context',
                        'custom',
                        'breadcrumbs'
                    ], breadcrumbs));
                }
                if (isNotEmpty(payload.transactions) && transactionBreadcrumbsFilter) {
                    const breadcrumbs = getBreadcrumbs().filter(transactionBreadcrumbsFilter);
                    if (isNotEmpty(breadcrumbs)) {
                        payload.transactions = payload.transactions.map(assocPath([
                            'context',
                            'custom',
                            'breadcrumbs'
                        ], breadcrumbs));
                    }
                }
                return payload;
            };
            apm.addFilter(filterHandler);
            return ()=>{
                // Well, same as with observers this is the only solution :(
                const apmConfigService = apm.serviceFactory.instances.ConfigService;
                apmConfigService.filters = apmConfigService.filters.filter((filter)=>filter !== filterHandler);
            };
        }
    }, [
        apm,
        getBreadcrumbs,
        transactionBreadcrumbsFilter
    ]);
    useEffect(()=>{
        const cleanupFns = instruments.filter((type)=>type !== instrumentTypes.APM_TRANSACTION).map((instrumentType)=>addBasicInstrumentationHandler({
                type: instrumentType,
                callback: setBreadcrumb,
                transformer: getInstrumentTransformer(instrumentType, breadcrumbTransformers)
            }, additionalHandlers));
        return ()=>{
            cleanupFns.forEach((fn)=>fn?.());
        };
    }, [
        breadcrumbTransformers,
        instruments,
        setBreadcrumb,
        additionalHandlers
    ]);
};
